import { forwardRef, useMemo } from 'react';
import { useAuthStore } from '@/stores/auth-store';

interface CreditNoteTicketProps {
  creditNote: any;
  originalSale?: any;
}

export const CreditNoteTicket = forwardRef<HTMLDivElement, CreditNoteTicketProps>(({ creditNote, originalSale }, ref) => {
  const { user } = useAuthStore();

  if (!creditNote) return null;

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return new Date().toLocaleString('es-MX');
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return new Date().toLocaleString('es-MX');
    return d.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const items = useMemo(() => {
    let raw = creditNote.items;
    if (!raw) return [];
    try {
      if (typeof raw === 'string') raw = JSON.parse(raw);
    } catch {
      return [];
    }
    if (!Array.isArray(raw)) return [];
    return raw.map((item: any) => {
      // Enriquecer nombre desde la venta original si falta
      const fromSale = originalSale?.items?.find(
        (si: any) => si.productId === item.productId,
      );
      return {
        ...item,
        name: item.name || fromSale?.product?.name || fromSale?.name || 'Producto',
        quantity: Number(item.quantity || 0),
        unitPrice: Number(item.unitPrice || item.price || 0),
        netTotal: Number(
          item.netTotal ??
            Number(item.quantity || 0) * Number(item.unitPrice || item.price || 0),
        ),
      };
    });
  }, [creditNote.items, originalSale]);

  const total = Number(creditNote.total || 0);
  const remaining = Number(creditNote.remaining ?? creditNote.total ?? 0);
  const subtotal = Number(creditNote.subtotal || total);
  const taxTotal = Number(creditNote.taxTotal || 0);
  const saleFolio = creditNote.originalSale?.folio || originalSale?.folio || '—';
  const noteFolio = creditNote.folio || `NC-${String(creditNote.id || '').slice(-6).toUpperCase()}`;

  return (
    <div ref={ref} className="bg-white p-4 text-[12px] font-mono text-black w-[80mm] mx-auto leading-tight print-ticket">
      <div className="text-center mb-4">
        <h1 className="font-bold text-[16px] mb-1">{user?.company?.name || 'MI EMPRESA'}</h1>
        <p className="whitespace-pre-wrap">{user?.company?.ticketHeader || ''}</p>
        <p>Sucursal: {user?.branch?.name || 'Principal'}</p>
        <p>Fecha: {formatDate(creditNote.createdAt)}</p>
        <p className="font-bold mt-2 text-[14px]">NOTA DE CRÉDITO</p>
        <p className="font-bold text-[13px]">Folio: {noteFolio}</p>
        <p>Venta origen: {saleFolio}</p>
        <p>Atendió: {creditNote.createdBy?.fullName || user?.fullName || '—'}</p>
      </div>

      <div className="border-t border-dashed border-black my-2"></div>

      <table className="w-full mb-2">
        <thead>
          <tr className="border-b border-dashed border-black">
            <th className="text-left py-1 w-[15%]">CANT</th>
            <th className="text-left py-1">DESCRIPCIÓN</th>
            <th className="text-right py-1">IMPORTE</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item: any, idx: number) => (
              <tr key={idx}>
                <td className="align-top py-1 text-center">{item.quantity}</td>
                <td className="align-top py-1 pr-1">{item.name}</td>
                <td className="align-top text-right py-1">${item.netTotal.toFixed(2)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className="text-center py-2 text-[10px]">
                (Sin detalle de productos)
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2"></div>

      <div className="flex justify-between mb-1">
        <span>SUBTOTAL:</span>
        <span>${subtotal.toFixed(2)}</span>
      </div>
      {taxTotal > 0 && (
        <div className="flex justify-between mb-1 text-[11px]">
          <span>IVA / IMPUESTOS:</span>
          <span>${taxTotal.toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between mb-1 font-bold text-[16px]">
        <span>TOTAL DEVUELTO:</span>
        <span>${total.toFixed(2)}</span>
      </div>

      <div className="border-t border-dashed border-black my-2"></div>

      <div className="text-center mb-2">
        <p className="font-bold text-[14px]">SALDO DISPONIBLE: ${remaining.toFixed(2)}</p>
        <p className="text-[10px]">
          Presente este folio al cobrar: <strong>{noteFolio}</strong>
        </p>
        <p className="text-[10px] mt-1">
          Método de pago → Nota de Crédito → escribir el folio
        </p>
      </div>

      {creditNote.reason && (
        <>
          <div className="border-t border-dashed border-black my-2"></div>
          <div className="text-center">
            <p className="font-bold">MOTIVO:</p>
            <p>{creditNote.reason}</p>
          </div>
        </>
      )}

      <div className="text-center mt-6">
        <p>¡Gracias por su preferencia!</p>
        <p className="whitespace-pre-wrap mt-2">{user?.company?.ticketFooter || ''}</p>
      </div>

      <style>{`
        @media print {
          @page { margin: 0; }
          body { margin: 0; }
          .print-ticket { width: 80mm !important; margin: 0 auto !important; }
        }
      `}</style>
    </div>
  );
});

CreditNoteTicket.displayName = 'CreditNoteTicket';
